// ---------------------------------------------------------------------------
// Digital Ghanti core service (schools, bells, rosters, users, notices, …).
// Base origins come from .env (REACT_APP_*), with a local fallback so the app
// still runs if the env var is missing. Change the value in .env per env.
// ---------------------------------------------------------------------------
export const BASE = process.env.REACT_APP_API_BASE || 'http://localhost:9090';

export const BASE_URL = BASE + '/api/v1';
const FILE_BASE = "/file";
export const FILE_BASE_URL = BASE + '/api/v1' + FILE_BASE;

// ---------------------------------------------------------------------------
// HRIS service — deployed as a separate backend (own host/port). Kept apart so
// it can point at a different origin without touching the core base above.
// ---------------------------------------------------------------------------
export const HRIS_BASE = process.env.REACT_APP_HRIS_BASE || 'https://api.digitalghanti.com/hris';

export const HRIS_BASE_URL = HRIS_BASE + '/api/v1';

const SCHOOL = "/school";
const ROSTER = "/roster";
const REMOTE = "/remote";
const EMERGENCY = "/emergency";
const BELL = "/bell";
const MUSIC = "/music";
const APP_BASE = "/app";
const MUSIC_LIBRARY = "/musicLibrary";
const META = "/meta";
const COMPANY_BASE = "/company";
const NOTICE_BASE = "/notice";
const VIRTUAL_ANNOUNCEMENT_BASE = "/virtual-announcement";
const USER_BASE = "/user";
const ACTIVITY_BASE = "/activity";
const BELL_LOG_BASE = "/bell-log";
const PLAY = "/play";
const PROFILE_MENU = "/profile_menu";
const PAYMENT = "/payment";
const ADVERTISEMENT_BASE = "/advertisement";

const FILES = "/files";
const TYPE = "/type";
const LOGIN = "/login";
const DETAIL = "/detail";
const UPLOAD = "/upload";
const UPLOADS = "/uploads";
const SEARCH = "/search";
const QUEUE = "/queue";
const EDIT = "/update";
const DELETE = "/delete";
const RECONCILE = "/reconcile";
const ACTIVE = "/active";
const MIN = "/min";
const BATCH = "/batch";
const LIVE = "/live";


export const schoolConstants = {
    SCHOOL_SAVE: BASE_URL.concat(SCHOOL),
    SCHOOL_DETAIL: BASE_URL.concat(SCHOOL),
    SCHOOL_META_DETAIL: BASE_URL.concat(SCHOOL.concat("/meta")),
    SCHOOL_META_UPDATE: BASE_URL.concat(SCHOOL.concat("/meta")),
    SEARCH_SEARCH: BASE_URL.concat(SCHOOL).concat(SEARCH),
    FETCH_SCHOOL_FOR_DROPDOWN: BASE_URL.concat(SCHOOL).concat(ACTIVE.concat(MIN)),
};

// School payments — POST /payment to record a payment, GET /payment/search to list.
export const paymentConstants = {
    PAYMENT_SAVE: BASE_URL.concat(PAYMENT),
    PAYMENT_SEARCH: BASE_URL.concat(PAYMENT).concat(SEARCH),
};

// Profile menu (services) — GET /profile-menu/active/min → active min info list.
export const profileMenuConstants = {
    FETCH_ACTIVE_MIN: BASE_URL.concat(PROFILE_MENU).concat(ACTIVE).concat(MIN),
};

export const fileApiConstants = {
    UPLOAD_FILE: BASE_URL.concat(FILE_BASE.concat(UPLOAD)),
    UPLOAD_FILES: BASE_URL.concat(FILE_BASE.concat(UPLOADS)),
    DELETE_FILE: BASE_URL.concat(FILE_BASE),
    ALLFILE: BASE_URL.concat(FILE_BASE.concat("/all")),
};

export const rosterConstants = {
    ROSTER_SAVE: BASE_URL.concat(ROSTER),
    ROSTER_SEARCH: BASE_URL.concat(ROSTER).concat("/search"),
    ROSTER_DELETE: BASE_URL.concat(ROSTER).concat(DELETE),
};

export const bellRemoteConstants = {
    BELL_SWITCH: BASE_URL.concat(REMOTE),
};

export const dashboardConstants = {
    FETCH_DASHBOARD_TILES: BASE_URL.concat("/dashboard/tiles"),
    FETCH_DASHBOARD_DONUT_CHART: BASE_URL.concat("/dashboard/pieChart"),
    // Admin sales overview line chart → { numbers, months }.
    FETCH_SALES_OVERVIEW: BASE_URL.concat("/dashboard/salesOverview"),
};

export const schoolDashboardConstants = {
    FETCH_DASHBOARD_TILES: BASE_URL.concat("/school/dashboard/tiles"),
    FETCH_DASHBOARD_DONUT_CHART: BASE_URL.concat("/dashboard/pieChart"),
};

export const emergencyConstants = {
    PLAY_EMERGENCY_BELL: BASE_URL.concat(EMERGENCY.concat(PLAY)),
};

export const bellConstants = {
    BELL_SAVE: BASE_URL.concat(BELL),
    BELL_SEARCH: BASE_URL.concat(BELL.concat(SEARCH)),
    BELL_QUEUE: BASE_URL.concat(BELL.concat(QUEUE)),
    BELL_UPDATE: BASE_URL.concat(BELL.concat(EDIT)),
    BELL_DELETE: BASE_URL.concat(BELL.concat(DELETE)),
    // Marquee ticker config (colour, message, speed) for the bell screen.
    BELL_MARQUEE: BASE_URL.concat(BELL.concat("/marquee")),
};

export const metaConstants = {
    META_SEARCH: BASE_URL.concat(META.concat(SEARCH)),
};


export const userApiConstants = {
    SAVE_USER: BASE_URL.concat(USER_BASE),
    APPROVE_USER: BASE_URL.concat(USER_BASE.concat("/approve")),
    BLOCK_USER: BASE_URL.concat(USER_BASE.concat("/block")),
    SEARCH_USERS: BASE_URL.concat(USER_BASE.concat(SEARCH)),
    USER_DETAIL: BASE_URL.concat(USER_BASE.concat("/detail")),
    RESET_PASSWORD: BASE_URL.concat(USER_BASE.concat("/reset-password")),
    // Clear a user's failed-login counter so a locked-out account can sign in again.
    RESET_ATTEMPTS: BASE_URL.concat(USER_BASE.concat("/reset-attempts")),
};

export const activityApiConstants = {
    SEARCH_ACTIVITIES: BASE_URL.concat(ACTIVITY_BASE.concat(SEARCH)),
};

export const noticeApiConstants = {
    SAVE_NOTICE: BASE_URL.concat(NOTICE_BASE),
    DELETE_NOTICE: BASE_URL.concat(NOTICE_BASE.concat("/delete")),
    SEARCH_NOTICES: BASE_URL.concat(NOTICE_BASE.concat(SEARCH))
}

// Virtual announcements — batch upsert (POST array) and list (GET). The school
// is resolved from the authenticated token, so no schoolId is ever sent.
export const virtualAnnouncementApiConstants = {
    SAVE_VIRTUAL_ANNOUNCEMENTS: BASE_URL.concat(VIRTUAL_ANNOUNCEMENT_BASE),
    LIST_VIRTUAL_ANNOUNCEMENTS: BASE_URL.concat(VIRTUAL_ANNOUNCEMENT_BASE),
};

export const bellMusicApiConstants = {
    SAVE_BELL_MUSIC: BASE_URL.concat(BELL).concat(MUSIC),
    DELETE_BELL_MUSIC: BASE_URL.concat(BELL.concat(MUSIC).concat("/delete")),
    SEARCH_BELL_MUSIC: BASE_URL.concat(BELL.concat(MUSIC).concat(SEARCH)),
    BELL_MUSIC_FOR_DROPDOWN: BASE_URL.concat(BELL.concat(MUSIC).concat(ACTIVE).concat(MIN)),
};

// Music Library (Music Studio) — school app endpoints. The uploading admin is
// captured from the auth token (returned as `uploadedBy`), so it is never sent.
// List/detail return only active (status "Y") records.
export const musicLibraryApiConstants = {
    // POST → create (201, no body). GET → list (active, newest first).
    SAVE_MUSIC: BASE_URL.concat(MUSIC_LIBRARY),
    LIST_MUSIC: BASE_URL.concat(MUSIC_LIBRARY),
    // GET /{id} → single active track. Append the id at call time.
    MUSIC_DETAIL: BASE_URL.concat(MUSIC_LIBRARY),
};

// Advertisement admin CRUD. Save is an upsert (send `id` to edit, omit to
// create) and returns 201 with an empty body. Search takes no body and returns
// { advertisementList, size }. Delete is a soft delete (sets status "D").
export const advertisementApiConstants = {
    SAVE_ADVERTISEMENT: BASE_URL.concat(ADVERTISEMENT_BASE),
    SEARCH_ADVERTISEMENT: BASE_URL.concat(ADVERTISEMENT_BASE).concat(SEARCH),
    DELETE_ADVERTISEMENT: BASE_URL.concat(ADVERTISEMENT_BASE).concat(DELETE),
    // App-facing read: only active + enabled ads for the client carousel.
    // GET → { speed, settings: [{ id, title, description, url, enabled }] }.
    APP_ADVERTISEMENT: BASE_URL.concat(APP_BASE).concat(ADVERTISEMENT_BASE),
};

export const loginApiConstants = {
    LOGIN_USER: BASE_URL.concat(LOGIN),
};

export const bellLogApiConstants = {
    // Frontend scheduler posts one event every time a bell plays.
    LOG_BELL: BASE_URL.concat(BELL_LOG_BASE),
    // Flush an offline queue of bell events in one request.
    LOG_BELL_BATCH: BASE_URL.concat(BELL_LOG_BASE).concat(BATCH),
    // Admin day-wise / filtered search, paginated and newest first.
    SEARCH_BELL_LOG: BASE_URL.concat(BELL_LOG_BASE).concat(SEARCH),
    // Realtime feed of the latest bell events for the logged-in school.
    LIVE_BELL_LOG: BASE_URL.concat(BELL_LOG_BASE).concat(LIVE),
    // Leaderboard: top schools by total bells played (?limit=10).
    TOP_SCHOOLS: BASE_URL.concat(BELL_LOG_BASE).concat("/top-schools"),
    // Super-admin overall view: today's bell status across all active schools.
    // GET ?date=YYYY-MM-DD → { summary?, schools: [ per-school today status ] }.
    OVERALL_STATUS: BASE_URL.concat(BELL_LOG_BASE).concat("/overall"),
};
